import { ApolloClient, InMemoryCache, from } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { HttpLink } from '@apollo/client/link/http';
import { buildClientSchema } from 'graphql';
import auth from '@/plugins/auth-service';
import { i18n } from '@/plugins/i18n';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { withScalars } from 'apollo-link-scalars';
import { toZonedTime, fromZonedTime } from '@/utils/dateTimeFns';
import { createApolloAuthRecoveryLink } from './apollo-auth-recovery';
import { authTelemetry } from './authTelemetry';

const removeTypenameLink = removeTypenameFromVariables();

import introspectionResult from '@/gql/graphql.schema.json';

const schema = buildClientSchema(introspectionResult);

export const createApoloClient = (grapgUri, schemaCode, connectToDevTools) => {
  const httpLink = new HttpLink({
    uri: grapgUri,
  });

  const authLink = setContext(async (_, { headers }) => {
    const token = await auth.getAccessToken();
    return {
      headers: {
        ...headers,
        'Accept-Language': i18n.locale,
        Authorization: `Bearer ${token}`,
        schemaCode,
      },
    };
  });

  const onErrorLink = createApolloAuthRecoveryLink({
    auth,
    telemetry: authTelemetry,
  });

  const dateTimeSerialize = (parsed) => {
    const date = new Date(parsed);
    if (date instanceof Date) {
      return fromZonedTime(date).toISOString();
    }
    return parsed;
  };

  const dateTimeParse = (raw) => {
    if (!raw) return null;
    if (typeof raw === 'string') {
      return toZonedTime(raw);
    }
    throw new Error('invalid value to parse');
  };

  const typesMap = {
    DateTime: {
      serialize: dateTimeSerialize,
      parseValue: dateTimeParse,
    },
    DateTimeDateTimeOptional: {
      serialize: dateTimeSerialize,
      parseValue: dateTimeParse,
    },
  };

  return new ApolloClient({
    link: from([
      removeTypenameLink,
      authLink,
      withScalars({ schema, typesMap }),
      onErrorLink,
      httpLink,
    ]),
    cache: new InMemoryCache(),
    connectToDevTools,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};
